import api from '../config/api';

class EmployeePerformanceService {
  // Get comprehensive employee performance analytics
  static async getPerformanceAnalytics() {
    try {
      console.log('🔍 Fetching employee performance analytics from hosted API...');
      
      // Try to fetch employees from local backend for company-specific filtering
      try {
        console.log('🔄 Switching to local backend for company-specific employee data...');
        const response = await api.get('/employee-performance/analytics');
        
        console.log('📊 Local backend performance analytics response:', response.data);
        
        return {
          success: true,
          data: response.data.data
        };
      } catch (localError) {
        console.log('⚠️ Local backend failed, trying hosted API as fallback:', localError.message);
        
        const employeesResponse = await api.authApi.get('/employees');
        console.log('📊 Hosted API employees response (fallback):', employeesResponse.data);
        
        if (employeesResponse.data && employeesResponse.data.length > 0) {
          // Format the data for performance dashboard
          const performanceData = {
            employees: employeesResponse.data.map(emp => ({
              _id: emp._id || emp.id,
              name: emp.name || emp.teamMemberName || 'Unknown',
              email: emp.email || emp.teamMemberEmail || 'N/A',
              role: emp.role || 'Employee',
              department: emp.department || { name: 'Sales' },
              company: emp.company || { name: 'Company' },
              totalLeads: 0,
              convertedLeads: 0,
              conversionRate: 0,
              totalValue: 0,
              averageDealSize: 0,
              performanceScore: Math.floor(Math.random() * 40) + 60 // Random score for demo
            })),
            summary: {
              totalEmployees: employeesResponse.data.length,
              activeEmployees: employeesResponse.data.filter(emp => emp.isActive !== false).length,
              averagePerformance: 75,
              totalRevenue: 0
            }
          };
          
          return {
            success: true,
            data: performanceData
          };
        }
      }
    } catch (error) {
      console.error('Error fetching performance analytics:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch performance analytics'
      };
    }
  }

  // Get detailed performance for individual employee
  static async getEmployeeDetails(employeeId) {
    try {
      console.log(`🔍 Fetching detailed performance for employee: ${employeeId}`);
      
      // Try hosted API first
      try {
        const employeeResponse = await api.authApi.get(`/employees/${employeeId}`);
        console.log('📊 Hosted API employee details response:', employeeResponse.data);
        
        if (employeeResponse.data) {
          const emp = employeeResponse.data;
          const detailedData = {
            employee: {
              _id: emp._id || emp.id,
              name: emp.name || emp.teamMemberName || 'Unknown',
              email: emp.email || emp.teamMemberEmail || 'N/A',
              role: emp.role || 'Employee',
              department: emp.department || { name: 'Sales' },
              company: emp.company || { name: 'Company' }
            },
            performance: {
              totalLeads: 0,
              convertedLeads: 0,
              conversionRate: 0,
              totalValue: 0,
              averageDealSize: 0,
              performanceScore: Math.floor(Math.random() * 40) + 60
            },
            recentLeads: [],
            monthlyStats: []
          };
          
          return {
            success: true,
            data: detailedData
          };
        }
      } catch (hostedError) {
        console.log('⚠️ Hosted API failed for employee details, falling back to local backend:', hostedError.message);
      }
      
      // Fallback to local backend
      const response = await api.get(`/employee-performance/employee/${employeeId}`);
      
      console.log('📊 Local backend employee details response:', response.data);
      
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error fetching employee details:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch employee details'
      };
    }
  }

  // Get team performance comparison
  static async getTeamComparison() {
    try {
      console.log('🔍 Fetching team performance comparison...');
      const response = await api.get('/employee-performance/team-comparison');
      
      console.log('📊 Team comparison response:', response.data);
      
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error fetching team comparison:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch team comparison'
      };
    }
  }

  // Helper methods for data formatting
  static formatCurrency(amount) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  }

  static formatPercentage(value) {
    return `${parseFloat(value || 0).toFixed(1)}%`;
  }

  static formatDate(date) {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  static getPerformanceColor(score) {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-blue-600 bg-blue-100';
    if (score >= 40) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  }

  static getStatusColor(status) {
    const colors = {
      'new': 'bg-yellow-100 text-yellow-800',
      'contacted': 'bg-purple-100 text-purple-800',
      'qualified': 'bg-indigo-100 text-indigo-800',
      'proposal': 'bg-blue-100 text-blue-800',
      'negotiation': 'bg-orange-100 text-orange-800',
      'closed_won': 'bg-green-100 text-green-800',
      'closed_lost': 'bg-red-100 text-red-800',
      'on_hold': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }

  static getWorkloadLevel(percentage) {
    if (percentage >= 80) return { level: 'High', color: 'text-red-600' };
    if (percentage >= 60) return { level: 'Medium', color: 'text-yellow-600' };
    if (percentage >= 30) return { level: 'Low', color: 'text-green-600' };
    return { level: 'Very Low', color: 'text-gray-600' };
  }
}

export default EmployeePerformanceService;
